import { defineStore } from 'pinia';
import axios from 'axios';

function normalizeMarketToken(value) {
    return String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function extractMarketCandidates(input) {
    if (!input) return [];
    if (typeof input === 'string') return [input];
    if (typeof input !== 'object') return [];
    return [
        input.market_name,
        input.name,
        input.symbol,
        input.market,
        input.futures_symbol,
    ].filter(Boolean);
}

function ensureArrayBucket(map, key) {
    if (!map[key] || !Array.isArray(map[key])) {
        map[key] = [];
    }
    return map[key];
}

function upsertById(list, item, { prepend = false } = {}) {
    const idx = list.findIndex(o => o?.id === item?.id);
    if (idx > -1) {
        list[idx] = { ...list[idx], ...item };
        return { inserted: false, updated: true };
    }
    if (prepend) list.unshift(item);
    else list.push(item);
    return { inserted: true, updated: false };
}

function removeById(list, id) {
    const idx = list.findIndex(o => o?.id === id);
    if (idx > -1) {
        list.splice(idx, 1);
        return true;
    }
    return false;
}

function bumpMetaTotal(metaMap, key, delta) {
    if (!metaMap || !key) return;
    const meta = metaMap[key];
    if (!meta || meta.total == null) return;
    const next = (parseInt(meta.total, 10) || 0) + delta;
    meta.total = Math.max(0, next);
}

function tradeFingerprint(trade) {
    const price = String(trade?.price ?? '');
    const qty = String(trade?.qty ?? trade?.quantity ?? trade?.amount ?? '');
    const side = String(trade?.side ?? '');
    const ts = String(trade?.created_at ?? trade?.time ?? '');
    return `${price}|${qty}|${side}|${ts}`;
}

export const useFuturesStore = defineStore('futures', {
    state: () => ({
        // Account balance and margin info
        account: null,
        
        // Positions by market
        positions: {},
        positionsMeta: {},
        
        // Open limit orders by market
        openOrders: {},
        openOrdersMeta: {},
        
        // Order book by market (futures-specific)
        orderbook: {},
        
        // Trade history by market
        trades: {},
        
        // Order history (all markets)
        orderHistory: [],
        orderHistoryMeta: {},
        
        // Trade history (all markets) 
        tradeHistory: [],
        tradeHistoryMeta: {},
        
        // Position history (closed positions)
        positionHistory: [],
        positionHistoryMeta: {},
        
        // Funding payments
        fundingPayments: [],
        fundingPaymentsMeta: {},

        // Market alias map for canonical buckets
        marketAliases: {},
        
        // Loading states
        loading: {
            account: false,
            positions: false,
            orders: false,
            orderHistory: false,
            trades: false,
            positionHistory: false,
            margin: false,
        },
    }),

    getters: {
        getAccount: (state) => state.account,
        
        getPositions: (state) => (market) => {
            const key = state.marketAliases?.[market]
                || state.marketAliases?.[normalizeMarketToken(market)]
                || market;
            return state.positions?.[key] || [];
        },
        
        getAllPositions: (state) => {
            return Object.values(state.positions).flat();
        },
        
        getOpenOrders: (state) => (market) => {
            const key = state.marketAliases?.[market]
                || state.marketAliases?.[normalizeMarketToken(market)]
                || market;
            return state.openOrders?.[key] || [];
        },

        getOpenOrdersTotal: (state) => (market) => {
            const key = state.marketAliases?.[market]
                || state.marketAliases?.[normalizeMarketToken(market)]
                || market;
            const metaTotal = state.openOrdersMeta?.[key]?.total;
            if (metaTotal != null) {
                const parsed = parseInt(metaTotal, 10);
                if (Number.isFinite(parsed)) return parsed;
            }
            return state.openOrders?.[key]?.length || 0;
        },
        
        getAllOpenOrders: (state) => {
            return Object.values(state.openOrders).flat();
        },
        
        getOrderbook: (state) => (market, type) => {
            if (!state.orderbook[market]) return [];
            if (type === 'bids') return state.orderbook[market].bids || [];
            if (type === 'asks') return state.orderbook[market].asks || [];
            return state.orderbook[market];
        },
        
        getTrades: (state) => (market) => {
            return state.trades?.[market] || [];
        },
        
        getOrderHistory: (state) => state.orderHistory,
        getTradeHistory: (state) => state.tradeHistory,
        getPositionHistory: (state) => state.positionHistory,
        getFundingPayments: (state) => state.fundingPayments,
        
        isLoading: (state) => (key) => state.loading[key] || false,
    },

    actions: {
        resolveMarketKey(input, fallback = null) {
            const candidates = extractMarketCandidates(input);
            for (const candidate of candidates) {
                if (this.marketAliases[candidate]) return this.marketAliases[candidate];
                const normalized = normalizeMarketToken(candidate);
                if (this.marketAliases[normalized]) return this.marketAliases[normalized];
            }

            const chosen = fallback || candidates[0] || null;
            return chosen;
        },

        registerMarketKey(canonical, aliases = []) {
            const canonicalKey = this.resolveMarketKey(canonical, typeof canonical === 'string' ? canonical : null);
            if (!canonicalKey) return null;

            const allAliases = [canonical, ...aliases];
            for (const aliasInput of allAliases) {
                const aliasCandidates = extractMarketCandidates(aliasInput);
                for (const candidate of aliasCandidates) {
                    this.marketAliases[candidate] = canonicalKey;
                    const normalized = normalizeMarketToken(candidate);
                    if (normalized) {
                        this.marketAliases[normalized] = canonicalKey;
                    }
                }
            }

            this.marketAliases[canonicalKey] = canonicalKey;
            const canonicalNormalized = normalizeMarketToken(canonicalKey);
            if (canonicalNormalized) {
                this.marketAliases[canonicalNormalized] = canonicalKey;
            }

            return canonicalKey;
        },

        // Fetch account balance
        async fetchAccount(route, params = {}) {
            this.loading.account = true;
            try {
                const response = await axios.get(route, { params });
                this.account = response.data.data || response.data;
                return this.account;
            } catch (error) {
                console.error('Failed to fetch futures account:', error);
                throw error;
            } finally {
                this.loading.account = false;
            }
        },

        setPositionMode(mode) {
            if (this.account) {
                this.account.position_mode = mode;
            }
        },

        // Fetch positions for a market
        async fetchPositions({ market, route, params = {} }) {
            this.loading.positions = true;
            try {
                const marketKey = this.registerMarketKey(market, [market]) || market;
                const response = await axios.get(route, { params: { market: marketKey, ...params } });
                const data = response.data.data || response.data;
                const rows = Array.isArray(data) ? data : [];

                for (const row of rows) {
                    this.registerMarketKey(marketKey, [row?.market_name, row?.symbol, row?.market]);
                }

                this.positions[marketKey] = rows;
                this.positionsMeta[marketKey] = response.data.meta || {};
                return this.positions[marketKey];
            } catch (error) {
                console.error('Failed to fetch futures positions:', error);
                throw error;
            } finally {
                this.loading.positions = false;
            }
        },

        // Add isolated margin to an open position
        async addPositionMargin({ route, positionId, amount, idempotencyKey = null, propAccountUuid = null }) {
            this.loading.margin = true;
            try {
                const response = await axios.post(route, {
                    position_id: positionId,
                    amount,
                    idempotency_key: idempotencyKey,
                    prop_account_uuid: propAccountUuid || undefined,
                });

                const payload = response.data || {};
                if (payload.success && payload.position_id) {
                    this.patchPositionById(payload.position_id, {
                        initial_margin: payload.initial_margin,
                        isolated_margin_extra: payload.isolated_margin_extra,
                        liq_price: payload.liq_price,
                    });
                }

                return payload;
            } catch (error) {
                console.error('Failed to add isolated margin:', error);
                throw error;
            } finally {
                this.loading.margin = false;
            }
        },

        // Remove isolated margin from an open position
        async removePositionMargin({ route, positionId, amount, idempotencyKey = null, propAccountUuid = null }) {
            this.loading.margin = true;
            try {
                const response = await axios.post(route, {
                    position_id: positionId,
                    amount,
                    idempotency_key: idempotencyKey,
                    prop_account_uuid: propAccountUuid || undefined,
                });

                const payload = response.data || {};
                if (payload.success && payload.position_id) {
                    this.patchPositionById(payload.position_id, {
                        initial_margin: payload.initial_margin,
                        isolated_margin_extra: payload.isolated_margin_extra,
                        liq_price: payload.liq_price,
                    });
                }

                return payload;
            } catch (error) {
                console.error('Failed to remove isolated margin:', error);
                throw error;
            } finally {
                this.loading.margin = false;
            }
        },

        // Fetch open orders for a market
        async fetchOpenOrders({ market, route, params = {} }) {
            this.loading.orders = true;
            try {
                const marketKey = this.registerMarketKey(market, [market]) || market;
                const response = await axios.get(route, { params: { market: marketKey, ...params } });
                const data = response.data.data || response.data;
                const rows = Array.isArray(data) ? data : [];

                for (const row of rows) {
                    this.registerMarketKey(marketKey, [row?.market_name, row?.symbol, row?.market]);
                }

                this.openOrders[marketKey] = rows;
                this.openOrdersMeta[marketKey] = response.data.meta || {};
                return this.openOrders[marketKey];
            } catch (error) {
                console.error('Failed to fetch futures open orders:', error);
                throw error;
            } finally {
                this.loading.orders = false;
            }
        },

        // Fetch order history
        async fetchOrderHistory({ route, params = {} }) {
            this.loading.orderHistory = true;
            try {
                const response = await axios.get(route, { params });
                this.orderHistory = response.data.data || [];
                this.orderHistoryMeta = response.data.meta || {};
                return this.orderHistory;
            } catch (error) {
                console.error('Failed to fetch futures order history:', error);
                throw error;
            } finally {
                this.loading.orderHistory = false;
            }
        },

        // Fetch trade history
        async fetchTradeHistory({ route, params = {} }) {
            this.loading.trades = true;
            try {
                const response = await axios.get(route, { params });
                this.tradeHistory = response.data.data || [];
                this.tradeHistoryMeta = response.data.meta || {};
                return this.tradeHistory;
            } catch (error) {
                console.error('Failed to fetch futures trade history:', error);
                throw error;
            } finally {
                this.loading.trades = false;
            }
        },

        // Fetch position history (closed positions)
        async fetchPositionHistory({ route, params = {} }) {
            this.loading.positionHistory = true;
            try {
                const response = await axios.get(route, { params });
                this.positionHistory = response.data.data || [];
                this.positionHistoryMeta = response.data.meta || {};
                return this.positionHistory;
            } catch (error) {
                console.error('Failed to fetch futures position history:', error);
                throw error;
            } finally {
                this.loading.positionHistory = false;
            }
        },

        // Fetch funding payments
        async fetchFundingPayments({ route, params = {} }) {
            try {
                const response = await axios.get(route, { params });
                this.fundingPayments = response.data.data || [];
                this.fundingPaymentsMeta = response.data.meta || {};
                return this.fundingPayments;
            } catch (error) {
                console.error('Failed to fetch funding payments:', error);
                throw error;
            }
        },

        // Initialize orderbook
        initOrderbook({ market, bids, asks }) {
            if (!this.orderbook[market]) {
                this.orderbook[market] = { bids: [], asks: [] };
            }
            if (bids) this.orderbook[market].bids = bids;
            if (asks) this.orderbook[market].asks = asks;
        },

        // Update orderbook from WebSocket
        updateOrderbook({ market, bids, asks }) {
            if (!this.orderbook[market]) {
                this.orderbook[market] = { bids: [], asks: [] };
            }
            if (bids) {
                for (const bid of bids) {
                    this.updateOrderbookSide(market, 'bids', bid);
                }
            }
            if (asks) {
                for (const ask of asks) {
                    this.updateOrderbookSide(market, 'asks', ask);
                }
            }
        },

        updateOrderbookSide(market, side, update) {
            const book = this.orderbook[market]?.[side] || [];
            const idx = book.findIndex(o => o.price === update.price);
            
            if (parseFloat(update.quantity) === 0) {
                // Remove price level
                if (idx > -1) {
                    book.splice(idx, 1);
                }
            } else if (idx > -1) {
                // Update existing
                book[idx] = update;
            } else {
                // Add new
                book.push(update);
            }
            
            // Sort: bids descending, asks ascending
            if (side === 'bids') {
                book.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
            } else {
                book.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
            }
        },

        // Initialize trades list from REST API
        initTrades({ market, trades }) {
            const source = Array.isArray(trades) ? trades : [];
            const seen = new Set();
            const unique = [];
            for (const t of source) {
                const key = t?.id ? `id:${t.id}` : `fp:${tradeFingerprint(t)}`;
                if (!seen.has(key)) {
                    seen.add(key);
                    unique.push(t);
                }
            }
            this.trades[market] = unique;
        },

        // Add trade to market trades
        addTrade({ market, trade }) {
            const trades = ensureArrayBucket(this.trades, market);

            // 1) Prefer strict dedupe by id
            if (trade?.id && trades.some(t => t?.id && t.id === trade.id)) {
                return;
            }

            // 2) Fallback dedupe by fingerprint for providers/events that don't send stable ids
            const fp = tradeFingerprint(trade);
            const isDuplicate = trades.slice(0, 30).some(t => tradeFingerprint(t) === fp);
            if (isDuplicate) {
                return;
            }

            trades.unshift(trade);
            if (trades.length > 100) {
                trades.splice(100);
            }
        },

        // Add multiple trades to market trades (batched path for high-TPS sockets)
        addTrades({ market, trades: incomingTrades }) {
            const list = ensureArrayBucket(this.trades, market);
            const source = Array.isArray(incomingTrades) ? incomingTrades : [];
            if (!source.length) return;

            // Newest first to match existing UI behavior.
            const ordered = source.slice().reverse();
            for (const trade of ordered) {
                // 1) Prefer strict dedupe by id
                if (trade?.id && list.some(t => t?.id && t.id === trade.id)) {
                    continue;
                }

                // 2) Fallback dedupe by fingerprint
                const fp = tradeFingerprint(trade);
                const isDuplicate = list.slice(0, 30).some(t => tradeFingerprint(t) === fp);
                if (isDuplicate) {
                    continue;
                }

                list.unshift(trade);
            }

            if (list.length > 100) {
                list.splice(100);
            }
        },

        // --- WebSocket Event Handlers ---

        // Drop realtime events that don't belong to the account currently in
        // view. Position/order rows are keyed by prop_account_id, which a
        // funded account's demo (challenge/verification) and real (funded)
        // futures accounts share — so a funded view additionally filters out
        // pre-funding records by created_at against prop_funded_at.
        _recordInScope(rec) {
            if (!rec) return false;
            const activeProp = Number(this.account?.prop_account_id ?? 0);
            const recProp = Number(rec.prop_account_id ?? 0);
            if (recProp !== activeProp) return false;
            if (activeProp > 0) {
                const fundedAt = this.account?.prop_funded_at;
                if (fundedAt && rec.created_at && String(rec.created_at) < String(fundedAt)) {
                    return false;
                }
            }
            return true;
        },

        // Handle account update from WebSocket
        handleAccountUpdated(payload) {
            const account = payload?.account || payload;
            if (!account) return;
            // Only apply updates for the account currently in view; otherwise a
            // regular- or demo-account balance broadcast (same user channel)
            // would overwrite the funded prop balance.
            if (this.account?.id && account.id && Number(account.id) !== Number(this.account.id)) {
                return;
            }
            this.account = { ...this.account, ...account };
        },

        // Handle position update from WebSocket
        handlePositionUpdated(payload) {
            const position = payload?.position || payload;
            if (!this._recordInScope(position)) return;
            const market = this.registerMarketKey(
                position?.market_name || position?.symbol || position?.market,
                [position?.market_name, position?.symbol, position?.market]
            );
            if (!position?.id || !market) return;

            const list = ensureArrayBucket(this.positions, market);

            const qty = parseFloat(position.qty ?? position.quantity ?? 0);
            const terminal = ['closed', 'liquidated', 'cancelled', 'failed'].includes(String(position.status || '').toLowerCase());
            if (terminal || qty === 0) {
                for (const m of Object.keys(this.positions)) {
                    removeById(this.positions[m], position.id);
                }
                return;
            }

            upsertById(list, position, { prepend: true });
        },

        // Handle position closed
        handlePositionClosed(payload) {
            const position = payload?.position || payload;
            const id = position?.id;
            if (!id) return;

            for (const m of Object.keys(this.positions)) {
                removeById(this.positions[m], id);
            }
        },

        // Handle order created
        handleOrderCreated(payload) {
            const order = payload?.order || payload;
            if (!this._recordInScope(order)) return;
            const market = this.registerMarketKey(
                order?.market_name || order?.symbol || order?.market,
                [order?.market_name, order?.symbol, order?.market]
            );
            if (!order?.id || !market) return;

            if (order.type === 'market') return;

            const list = ensureArrayBucket(this.openOrders, market);
            const { inserted } = upsertById(list, order, { prepend: true });
            if (inserted) bumpMetaTotal(this.openOrdersMeta, market, +1);
        },

        // Handle order updated
        handleOrderUpdated(payload) {
            const order = payload?.order || payload;
            if (!this._recordInScope(order)) return;
            const market = this.registerMarketKey(
                order?.market_name || order?.symbol || order?.market,
                [order?.market_name, order?.symbol, order?.market]
            );
            const id = order?.id;
            if (!id || !market) return;

            const list = ensureArrayBucket(this.openOrders, market);

            if (order.status && order.status !== 'open' && order.status !== 'partially_filled') {
                const removed = removeById(list, id);
                if (removed) bumpMetaTotal(this.openOrdersMeta, market, -1);
                return;
            }

            const { inserted } = upsertById(list, order);
            if (inserted) bumpMetaTotal(this.openOrdersMeta, market, +1);
        },

        // Handle order filled
        handleOrderFilled(payload) {
            const order = payload?.order || payload;
            const id = order?.id;
            if (!id) return;

            for (const m of Object.keys(this.openOrders)) {
                const removed = removeById(this.openOrders[m], id);
                if (removed) bumpMetaTotal(this.openOrdersMeta, m, -1);
            }
        },

        // Handle order cancelled
        handleOrderCancelled(payload) {
            this.handleOrderFilled(payload);
        },

        patchPositionById(positionId, patch = {}) {
            if (!positionId) return;
            for (const market of Object.keys(this.positions)) {
                const list = this.positions[market];
                if (!Array.isArray(list)) continue;
                const idx = list.findIndex((p) => p?.id === positionId);
                if (idx !== -1) {
                    list[idx] = { ...list[idx], ...patch };
                    return;
                }
            }
        },

        // Handle new trade
        handleTradeCreated(payload) {
            const trade = payload?.trade || payload;
            const market = this.registerMarketKey(
                trade?.market_name || trade?.symbol || trade?.market,
                [trade?.market_name, trade?.symbol, trade?.market]
            );
            if (!trade || !market) return;

            this.addTrade({ market, trade });
        },

        // Clear all data
        reset() {
            this.account = null;
            this.positions = {};
            this.positionsMeta = {};
            this.openOrders = {};
            this.openOrdersMeta = {};
            this.orderbook = {};
            this.trades = {};
            this.orderHistory = [];
            this.orderHistoryMeta = {};
            this.tradeHistory = [];
            this.tradeHistoryMeta = {};
            this.fundingPayments = [];
            this.fundingPaymentsMeta = {};
            this.marketAliases = {};
            this.loading.margin = false;
        },
    },
});
